import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from '../../hooks/useTranslation';

interface ImageEditorProps {
  imageUrl: string;
  onSave: (editedImage: string) => void;
  onCancel: () => void;
  aspectRatio?: number; // For story format (9:16)
}

interface ImageTransform {
  scale: number;
  x: number;
  y: number;
}

interface Point {
  x: number;
  y: number;
}

const MIN_SCALE = 0.1;
const MAX_SCALE = 4;
const ZOOM_STEP = 0.1;

const ImageEditor: React.FC<ImageEditorProps> = ({ 
  imageUrl, 
  onSave, 
  onCancel,
  aspectRatio = 9 / 16 
}) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  
  const [transform, setTransform] = useState<ImageTransform>({ 
    scale: 1, 
    x: 0, 
    y: 0
  });
  const [isDragging, setIsDragging] = useState(false);
  const [isPinching, setIsPinching] = useState(false);
  const [dragStart, setDragStart] = useState<Point>({ x: 0, y: 0 });
  const [lastPinchDistance, setLastPinchDistance] = useState(0);
  const [lastPinchCenter, setLastPinchCenter] = useState<Point>({ x: 0, y: 0 });
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [isAnimating, setIsAnimating] = useState(false);

  // Calculate container dimensions (story format: 9:16)
  const containerWidth = typeof window !== 'undefined' ? Math.min(375, window.innerWidth) : 375;
  const containerHeight = containerWidth / aspectRatio;

  // Calculate image bounds with proper constraints
  const getImageBounds = useCallback(() => {
    if (!imageRef.current || !imageLoaded) {
      return { minX: 0, maxX: 0, minY: 0, maxY: 0 };
    }

    const img = imageRef.current;
    const scaledWidth = img.naturalWidth * transform.scale;
    const scaledHeight = img.naturalHeight * transform.scale;

    const maxX = Math.max(0, (scaledWidth - containerWidth) / 2);
    const maxY = Math.max(0, (scaledHeight - containerHeight) / 2);

    return {
      minX: -maxX,
      maxX,
      minY: -maxY,
      maxY,
    };
  }, [transform.scale, imageLoaded, containerWidth, containerHeight]);

  // Constrain transform values
  const constrainTransform = useCallback((newTransform: ImageTransform): ImageTransform => {
    const bounds = getImageBounds();
    
    return {
      ...newTransform,
      scale: Math.max(MIN_SCALE, Math.min(MAX_SCALE, newTransform.scale)),
      x: Math.max(bounds.minX, Math.min(bounds.maxX, newTransform.x)),
      y: Math.max(bounds.minY, Math.min(bounds.maxY, newTransform.y)),
    };
  }, [getImageBounds]);

  // Handle image load with optimal initial positioning
  const handleImageLoad = useCallback(() => {
    if (imageRef.current) {
      const img = imageRef.current;
      const imgAspectRatio = img.naturalWidth / img.naturalHeight;
      const containerAspectRatio = aspectRatio;

      // Calculate initial scale to fill container
      let initialScale = 1;
      if (imgAspectRatio > containerAspectRatio) {
        // Image is wider - scale to fill height
        initialScale = (containerHeight / img.naturalHeight) * 1.1;
      } else {
        // Image is taller - scale to fill width
        initialScale = (containerWidth / img.naturalWidth) * 1.1;
      }

      // Ensure minimum scale
      initialScale = Math.max(initialScale, MIN_SCALE);

      setImageSize({ width: img.naturalWidth, height: img.naturalHeight });
      
      const initialTransform = constrainTransform({
        scale: initialScale,
        x: 0,
        y: 0,
      });
      
      setTransform(initialTransform);
      setImageLoaded(true);
    }
  }, [aspectRatio, containerWidth, containerHeight, constrainTransform]);

  // Get touch center point
  const getTouchCenter = useCallback((touches: React.TouchList): Point => {
    const touch1 = touches[0];
    const touch2 = touches[1];
    return {
      x: (touch1.clientX + touch2.clientX) / 2,
      y: (touch1.clientY + touch2.clientY) / 2,
    };
  }, []);

  // Get distance between two touches
  const getTouchDistance = useCallback((touch1: React.Touch, touch2: React.Touch): number => {
    return Math.hypot(
      touch2.clientX - touch1.clientX,
      touch2.clientY - touch1.clientY
    );
  }, []);

  // Handle drag start
  const handleStart = useCallback((clientX: number, clientY: number) => {
    if (isPinching) return;
    
    setIsDragging(true);
    setDragStart({
      x: clientX - transform.x,
      y: clientY - transform.y,
    });
  }, [transform.x, transform.y, isPinching]);

  // Handle drag move with constraints
  const handleMove = useCallback((clientX: number, clientY: number) => {
    if (!isDragging || isPinching) return;

    const newX = clientX - dragStart.x;
    const newY = clientY - dragStart.y;

    setTransform(prev => constrainTransform({
      ...prev,
      x: newX,
      y: newY,
    }));
  }, [isDragging, isPinching, dragStart, constrainTransform]);

  // Handle pinch zoom
  const handlePinchZoom = useCallback((
    distance: number,
    centerX: number,
    centerY: number
  ) => {
    if (lastPinchDistance === 0) {
      setLastPinchDistance(distance);
      setLastPinchCenter({ x: centerX, y: centerY });
      return;
    }

    const scaleChange = distance / lastPinchDistance;
    
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const containerCenterX = rect.left + rect.width / 2;
      const containerCenterY = rect.top + rect.height / 2;
      
      // Calculate zoom point relative to container
      const zoomPointX = centerX - containerCenterX;
      const zoomPointY = centerY - containerCenterY;
      
      setTransform(prev => {
        const newScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, prev.scale * scaleChange));
        const scaleRatio = newScale / prev.scale;
        
        // Adjust position to zoom towards touch point
        const newX = prev.x - (zoomPointX * (scaleRatio - 1));
        const newY = prev.y - (zoomPointY * (scaleRatio - 1));
        
        return constrainTransform({
          ...prev,
          scale: newScale,
          x: newX,
          y: newY,
        });
      });
    }

    setLastPinchDistance(distance);
    setLastPinchCenter({ x: centerX, y: centerY });
  }, [lastPinchDistance, constrainTransform]);

  // Handle end of interaction
  const handleEnd = useCallback(() => {
    setIsDragging(false);
    setIsPinching(false);
    setLastPinchDistance(0);
  }, []);

  // Mouse events
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    handleStart(e.clientX, e.clientY);
  }, [handleStart]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (isDragging) {
      e.preventDefault();
      handleMove(e.clientX, e.clientY);
    }
  }, [isDragging, handleMove]);

  const handleMouseUp = useCallback(() => {
    handleEnd();
  }, [handleEnd]);

  // Touch events with improved gesture handling
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    e.preventDefault();
    
    if (e.touches.length === 1) {
      const touch = e.touches[0];
      handleStart(touch.clientX, touch.clientY);
    } else if (e.touches.length === 2) {
      setIsPinching(true);
      setIsDragging(false);
      const distance = getTouchDistance(e.touches[0], e.touches[1]);
      const center = getTouchCenter(e.touches);
      setLastPinchDistance(distance);
      setLastPinchCenter(center);
    }
  }, [handleStart, getTouchDistance, getTouchCenter]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    e.preventDefault();
    
    if (e.touches.length === 1 && isDragging && !isPinching) {
      const touch = e.touches[0];
      handleMove(touch.clientX, touch.clientY);
    } else if (e.touches.length === 2 && isPinching) {
      const distance = getTouchDistance(e.touches[0], e.touches[1]);
      const center = getTouchCenter(e.touches);
      handlePinchZoom(distance, center.x, center.y);
    }
  }, [isDragging, isPinching, handleMove, handlePinchZoom, getTouchDistance, getTouchCenter]);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    e.preventDefault();
    if (e.touches.length === 0) {
      handleEnd();
    } else if (e.touches.length === 1) {
      setIsPinching(false);
      setLastPinchDistance(0);
      const touch = e.touches[0];
      handleStart(touch.clientX, touch.clientY);
    }
  }, [handleEnd, handleStart]);

  // Wheel zoom with smooth handling
  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    
    if (!containerRef.current) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const zoomPointX = e.clientX - rect.left - rect.width / 2;
    const zoomPointY = e.clientY - rect.top - rect.height / 2;
    
    const delta = e.deltaY > 0 ? 1 - ZOOM_STEP : 1 + ZOOM_STEP;
    
    setTransform(prev => {
      const newScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, prev.scale * delta));
      const scaleRatio = newScale / prev.scale;
      
      const newX = prev.x - (zoomPointX * (scaleRatio - 1));
      const newY = prev.y - (zoomPointY * (scaleRatio - 1));
      
      return constrainTransform({
        ...prev,
        scale: newScale,
        x: newX,
        y: newY,
      });
    });
  }, [constrainTransform]);

  // Zoom controls
  const handleZoomIn = useCallback(() => {
    setTransform(prev => {
      const newScale = Math.min(MAX_SCALE, prev.scale + ZOOM_STEP);
      return constrainTransform({ ...prev, scale: newScale });
    });
  }, [constrainTransform]);

  const handleZoomOut = useCallback(() => {
    setTransform(prev => {
      const newScale = Math.max(MIN_SCALE, prev.scale - ZOOM_STEP);
      return constrainTransform({ ...prev, scale: newScale });
    });
  }, [constrainTransform]);

  const handleResetZoom = useCallback(() => {
    if (!imageRef.current) return;
    
    const img = imageRef.current;
    const imgAspectRatio = img.naturalWidth / img.naturalHeight;
    const containerAspectRatio = aspectRatio;

    let resetScale = 1;
    if (imgAspectRatio > containerAspectRatio) {
      resetScale = (containerHeight / img.naturalHeight) * 1.1;
    } else {
      resetScale = (containerWidth / img.naturalWidth) * 1.1;
    }

    setIsAnimating(true);
    setTransform(constrainTransform({
      scale: Math.max(resetScale, MIN_SCALE),
      x: 0,
      y: 0,
    }));
    
    setTimeout(() => setIsAnimating(false), 300);
  }, [aspectRatio, containerWidth, containerHeight, constrainTransform]);


  // Apply constraints when scale changes
  useEffect(() => {
    if (imageLoaded) {
      setTransform(prev => constrainTransform(prev));
    }
  }, [transform.scale, imageLoaded, constrainTransform]);

  // Save edited image with high quality
  const handleSave = useCallback(() => {
    if (!imageRef.current || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Use higher resolution for better quality
    const scaleFactor = 2;
    canvas.width = containerWidth * scaleFactor;
    canvas.height = containerHeight * scaleFactor;

    // Enable high-quality rendering
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    // Draw background (black for story format)
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Calculate image position and size
    const img = imageRef.current;
    const scaledWidth = img.naturalWidth * transform.scale * scaleFactor;
    const scaledHeight = img.naturalHeight * transform.scale * scaleFactor;

    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;

    // Apply transformations
    ctx.save();
    ctx.translate(centerX + transform.x * scaleFactor, centerY + transform.y * scaleFactor);
    ctx.scale(transform.scale * scaleFactor, transform.scale * scaleFactor);

    // Draw image centered
    ctx.drawImage(
      img,
      -img.naturalWidth / 2,
      -img.naturalHeight / 2,
      img.naturalWidth,
      img.naturalHeight
    );

    ctx.restore();

    // Convert to blob with high quality
    canvas.toBlob((blob) => {
      if (blob) {
        const url = URL.createObjectURL(blob);
        onSave(url);
      }
    }, 'image/jpeg', 0.95);
  }, [transform, containerWidth, containerHeight, onSave]);

  const zoomPercentage = Math.round(transform.scale * 100);

  return (
    <div className="relative w-full h-full bg-black flex flex-col">
      {/* Editor Container */}
      <div 
        ref={containerRef}
        className="relative flex-1 overflow-hidden bg-black"
        style={{ 
          width: containerWidth, 
          height: containerHeight,
          margin: '0 auto',
          touchAction: 'none',
          cursor: isDragging ? 'grabbing' : 'grab',
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onWheel={handleWheel}
      >
        {/* Image */}
        <img
          ref={imageRef}
          src={imageUrl}
          alt="Edit"
          className="absolute select-none"
          style={{
            transform: `
              translate(${transform.x}px, ${transform.y}px) 
              scale(${transform.scale})
            `,
            transformOrigin: 'center center',
            width: imageSize.width || 'auto',
            height: imageSize.height || 'auto',
            maxWidth: 'none',
            maxHeight: 'none',
            userSelect: 'none',
            pointerEvents: 'none',
            transition: isAnimating ? 'transform 0.3s ease-out' : 'none',
            left: '50%',
            top: '50%',
            marginLeft: imageSize.width ? `-${imageSize.width / 2}px` : 0,
            marginTop: imageSize.height ? `-${imageSize.height / 2}px` : 0,
          }}
          onLoad={handleImageLoad}
          draggable={false}
        />
        <canvas ref={canvasRef} className="hidden" />
      </div>

      {/* Top Controls Bar */}
      <div className="absolute top-4 left-0 right-0 flex items-center justify-center px-4 z-20">
        {/* Zoom indicator */}
        <div className="bg-black bg-opacity-60 backdrop-blur-md text-white px-3 py-1.5 rounded-full text-xs font-medium">
          {zoomPercentage}%
        </div>
      </div>

      {/* Bottom Controls */}
      <div className="absolute bottom-4 left-0 right-0 z-20">
        {/* Zoom Controls */}
        <div className="flex justify-center gap-2 mb-3">
          <button
            onClick={handleZoomOut}
            disabled={transform.scale <= MIN_SCALE}
            className="w-10 h-10 bg-black bg-opacity-60 backdrop-blur-md text-white rounded-full flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed hover:bg-opacity-80 transition-all"
            aria-label={t("common.zoomOut")}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" />
            </svg>
          </button>
          
          <button
            onClick={handleResetZoom}
            className="w-10 h-10 bg-black bg-opacity-60 backdrop-blur-md text-white rounded-full flex items-center justify-center hover:bg-opacity-80 transition-all"
            aria-label={t("story.resetZoom")}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
          
          <button
            onClick={handleZoomIn}
            disabled={transform.scale >= MAX_SCALE}
            className="w-10 h-10 bg-black bg-opacity-60 backdrop-blur-md text-white rounded-full flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed hover:bg-opacity-80 transition-all"
            aria-label={t("common.zoomIn")}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7" />
            </svg>
          </button>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-center gap-3">
          <button
            onClick={onCancel}
            className="px-6 py-2.5 bg-gray-800 bg-opacity-70 backdrop-blur-md text-white rounded-full text-sm font-medium hover:bg-opacity-90 transition-all"
          >
            {t("common.cancel")}
          </button>
          
          <button
            onClick={handleSave}
            className="px-6 py-2.5 bg-blue-500 text-white rounded-full text-sm font-medium hover:bg-blue-600 transition-all shadow-lg"
          >
            {t("story.confirm")}
          </button>
        </div>
      </div>

      {/* Instructions hint */}
      {!imageLoaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 z-30">
          <div className="text-white text-center px-4">
            <p className="text-sm opacity-75">{t("story.loadingImage")}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageEditor;
